import React from 'react';
import { X } from 'lucide-react';
import { getCoachImage, hasCoachImage } from '../lib/coachImages';
import type { CoachPersona } from '../lib/personas';

interface CoachPersonaModalProps {
  coach: CoachPersona | null;
  isOpen: boolean;
  onClose: () => void;
}

const CoachPersonaModal: React.FC<CoachPersonaModalProps> = ({ coach, isOpen, onClose }) => {
  if (!isOpen || !coach) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className={`relative mb-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br ${coach.color} font-display text-4xl font-semibold text-white`}>
            {hasCoachImage(coach.id) ? (
              <img
                src={getCoachImage(coach.id)}
                alt={coach.name}
                className="h-full w-full object-cover"
              />
            ) : (
              coach.name.charAt(0)
            )}
          </div>

          <h2 className="font-display text-3xl font-bold text-slate-900">{coach.name}</h2>
          <p className="font-display text-md uppercase tracking-[0.2em] text-slate-500">{coach.title}</p>

          {/* Display headline with fallback to bio */}
          <p className="mt-4 font-body text-base leading-relaxed text-slate-700">
            {coach.headline || coach.bio || 'Professional coaching support'}
          </p>

          {/* Display full helpsAnswer list */}
          {coach.helpsAnswer && coach.helpsAnswer.length > 0 && (
            <div className="mt-4">
              <h3 className="font-display text-sm font-semibold text-slate-800 mb-2">Helps you with:</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                {coach.helpsAnswer.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {coach.highlightTags && coach.highlightTags.length > 0 && (
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {coach.highlightTags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachPersonaModal;
